import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Cookies from 'js-cookie';
import { prysmDB } from '../utils/database';
import apiService from '../services/apiService';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:4000';
const COOKIE_NAME = process.env.REACT_APP_COOKIE_NAME || 'prysm-registration';

const Profile = () => {
  const [userData, setUserData] = useState(null);
  const [selfieData, setSelfieData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [registerStatus, setRegisterStatus] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    const fetchUserData = async () => {
      try {
        const jwt = window.getAuthToken ? window.getAuthToken() : Cookies.get(COOKIE_NAME);
        if (!jwt) {
          // Try to reset session and get new token
          if (window.resetSession) {
            await window.resetSession();
            const newJwt = window.getAuthToken ? window.getAuthToken() : Cookies.get(COOKIE_NAME);
            if (!newJwt) {
              navigate('/register');
              return;
            }
          } else {
            navigate('/register');
            return;
          }
        }

        // Fetch user data
        const userResponse = await apiService.getUserData();

        if (!userResponse.ok) {
          throw new Error('Failed to fetch user data');
        }

        const userDataResponse = await userResponse.json();
        if (!userDataResponse.success) {
          throw new Error(userDataResponse.error?.message || 'Failed to fetch user data');
        }
        setUserData(userDataResponse.data);

        // Fetch selfie data
        const selfieResponse = await apiService.getImageStatus();

        if (selfieResponse.ok) {
          const selfieDataResponse = await selfieResponse.json();
          if (selfieDataResponse.success) {
            setSelfieData(selfieDataResponse.data);
          } else {
            console.error('Failed to fetch selfie data:', selfieDataResponse.error);
          }
        }

        setLoading(false);
      } catch (err) {
        console.error('Error fetching profile data:', err);
        setError(err.message || 'Failed to load profile data');
        setLoading(false);
      }
    };

    fetchUserData();
  }, [navigate]);

  const handleRegister = async () => {
    try {
      let jwt = window.getAuthToken ? window.getAuthToken() : Cookies.get(COOKIE_NAME);
      if (!jwt) {
        // Try to reset session and get new token
        if (window.resetSession) {
          await window.resetSession();
          jwt = window.getAuthToken ? window.getAuthToken() : Cookies.get(COOKIE_NAME);
          if (!jwt) {
            setError('Authentication required. Please log in again.');
            return;
          }
        } else {
          setError('Authentication required. Please log in again.');
          return;
        }
      }

      // Get all session storage items from storage and localStorage
      const sessionData = {};

      // Get data from storage stores
      const storesToCheck = [
        'registration_data',
        'visitorUUID',
        'square_nonce_info',
        'selfie_data',
        'license_data',
        'sms_send_result',
        'phoneNumber',
        'payment_data'
      ];

      for (const storeName of storesToCheck) {
        try {
          const data = await prysmDB.getAll(storeName);
          if (data && data.length > 0) {
            sessionData[storeName] = data;
          }
        } catch (error) {
          console.warn(`[Profile] Failed to get data from storage store ${storeName}:`, error);
        }
      }

      // Also collect remaining localStorage items
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        sessionData[`localStorage_${key}`] = localStorage.getItem(key);
      }

      const response = await apiService.registerUser(sessionData);

      const data = await response.json();

      if (response.ok && data.success) {
        setRegisterStatus('Registration submitted for review!');
        setError('');
        setTimeout(() => {
          setRegisterStatus('');
        }, 3000);
      } else {
        // Handle validation errors
        if (data.errors && Array.isArray(data.errors)) {
          setError(data.errors.join('\n'));
        } else if (data.error) {
          // Handle error object structure
          const errorMessage = typeof data.error === 'string'
            ? data.error
            : data.error.message || 'Failed to register';
          setError(errorMessage);
        } else {
          setError('Failed to register');
        }
        setRegisterStatus('');
      }
    } catch (err) {
      console.error('Registration error:', err);
      setError(err.message || 'Failed to register. Please try again.');
      setRegisterStatus('');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-gray-900 to-gray-800 flex items-center justify-center relative overflow-hidden">
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute -top-40 -right-40 w-80 h-80 bg-purple-900 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-blob"></div>
          <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-indigo-900 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-blob animation-delay-2000"></div>
          <div className="absolute top-40 left-40 w-80 h-80 bg-gray-800 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-blob animation-delay-4000"></div>
        </div>
        <div className="relative z-10">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-pink-500"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-gray-900 to-gray-800 flex items-center justify-center p-4 relative overflow-hidden">
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute -top-40 -right-40 w-80 h-80 bg-purple-900 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-blob"></div>
          <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-indigo-900 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-blob animation-delay-2000"></div>
          <div className="absolute top-40 left-40 w-80 h-80 bg-gray-800 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-blob animation-delay-4000"></div>
        </div>
        <div className="bg-gray-800/80 backdrop-blur-lg rounded-2xl shadow-2xl p-8 transform transition-all duration-500 hover:scale-[1.02] border border-gray-700 relative z-10">
          <div className="text-red-400 text-center">
            <p className="text-lg font-semibold">{error}</p>
            <button
              onClick={() => window.location.reload()}
              className="mt-4 px-4 py-2 bg-gradient-to-r from-pink-500 to-purple-600 text-white rounded-xl hover:from-pink-600 hover:to-purple-700 transition-all duration-300 w-full shadow-lg hover:shadow-xl transform hover:-translate-y-1"
            >
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-900 to-gray-800 flex items-center justify-center p-4 relative overflow-hidden">
      {/* Decorative background elements */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-purple-900 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-blob"></div>
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-indigo-900 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-blob animation-delay-2000"></div>
        <div className="absolute top-40 left-40 w-80 h-80 bg-gray-800 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-blob animation-delay-4000"></div>
      </div>

      <div className="w-full max-w-4xl relative z-10">
        <div className="bg-gray-800/80 backdrop-blur-lg rounded-2xl shadow-2xl p-8 transform transition-all duration-500 hover:scale-[1.02] border border-gray-700">
          <div className="text-center mb-8">
            <h1 className="text-4xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-pink-400 to-purple-500 mb-4 animate-fade-in">
              Registration Data
            </h1>
            <div className="h-1 w-20 bg-gradient-to-r from-pink-500 to-purple-600 mx-auto rounded-full animate-glow"></div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* User Information */}
            <div className="space-y-6">
              <h2 className="text-xl font-semibold text-gray-300">Personal Information</h2>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-400">Name</label>
                  <p className="mt-1 text-lg text-gray-200">
                    {userData?.first_name} {userData?.last_name}
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-400">Email</label>
                  <p className="mt-1 text-lg text-gray-200">{userData?.email}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-400">Facebook Profile</label>
                  <a
                    href={userData?.facebook_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mt-1 text-lg text-pink-400 hover:text-pink-300 transition-colors"
                  >
                    {userData?.facebook_url}
                  </a>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-400">Relationship Status</label>
                  <p className="mt-1 text-lg text-gray-200">{userData?.relationship_status}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-400">Relationship Type</label>
                  <p className="mt-1 text-lg text-gray-200">{userData?.relationship_type}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-400">Birthday</label>
                  <p className="mt-1 text-lg text-gray-200">{userData?.birthday}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-400">Zip Code</label>
                  <p className="mt-1 text-lg text-gray-200">{userData?.zip_code}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-400">Mobile Number</label>
                  <p className="mt-1 text-lg text-gray-200">{userData?.mobile_number}</p>
                </div>
              </div>
            </div>

            {/* Selfie Section */}
            <div className="space-y-6">
              <h2 className="text-xl font-semibold text-gray-300">Your Selfie</h2>
              {selfieData ? (
                <div className="space-y-4">
                  <div className="relative">
                    <img
                      src={selfieData.upload_path}
                      alt="Selfie"
                      className="w-full h-auto rounded-lg shadow-md border border-gray-700"
                    />
                  </div>
                  <div className="bg-gray-700/50 p-4 rounded-lg border border-gray-600">
                    <h3 className="text-sm font-medium text-gray-300">Verification Status</h3>
                    <p className="mt-1 text-sm text-gray-200">
                      {selfieData.process_status === 'processed'
                        ? 'Verified successfully'
                        : 'Processing...'}
                    </p>
                    {selfieData.faces_detected !== undefined && (
                      <div className="mt-2 space-y-2">
                        <p className="text-sm text-gray-200">
                          Faces detected: {selfieData.faces_detected}
                        </p>
                        {selfieData.image_analysis && (
                          <div className="space-y-2">
                            {selfieData.image_analysis.map((face, index) => (
                              <div key={index} className="bg-gray-700/50 p-3 rounded-lg border border-gray-600">
                                <p className="text-sm text-gray-200">
                                  Face {index + 1}:
                                </p>
                                <p className="text-sm text-gray-300">
                                  Age: {face.age || 'Unknown'}
                                </p>
                                <p className="text-sm text-gray-300">
                                  Gender: {face.gender || 'Unknown'}
                                </p>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                <div className="text-center py-8">
                  <p className="text-gray-400">No selfie uploaded yet</p>
                  <button
                    onClick={() => navigate('/upload-selfie')}
                    className="mt-4 px-4 py-2 bg-gradient-to-r from-pink-500 to-purple-600 text-white rounded-xl hover:from-pink-600 hover:to-purple-700 transition-all duration-300 w-full shadow-lg hover:shadow-xl transform hover:-translate-y-1"
                  >
                    Upload Selfie
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Add Register Button */}
          <div className="mt-8 text-center">
            <button
              onClick={handleRegister}
              className="px-6 py-3 bg-gradient-to-r from-pink-500 to-purple-600 text-white rounded-xl hover:from-pink-600 hover:to-purple-700 transform transition-all duration-200 hover:scale-[1.02] focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 shadow-md"
            >
              Register
            </button>
            {registerStatus && (
              <p className="mt-4 text-green-400">{registerStatus}</p>
            )}
            {error && (
              <div className="mt-4">
                <p className="text-red-400 font-semibold">Registration Issues:</p>
                <ul className="text-red-400 mt-2 list-disc list-inside">
                  {typeof error === 'string'
                    ? error.split('\n').map((error, index) => (
                      <li key={index}>{error}</li>
                    ))
                    : <li>{error}</li>
                  }
                </ul>
              </div>
            )}
          </div>
        </div>
      </div>

      <style jsx="true">{`
        @keyframes blob {
          0% {
            transform: translate(0px, 0px) scale(1);
          }
          33% {
            transform: translate(30px, -50px) scale(1.1);
          }
          66% {
            transform: translate(-20px, 20px) scale(0.9);
          }
          100% {
            transform: translate(0px, 0px) scale(1);
          }
        }
        .animate-blob {
          animation: blob 7s infinite;
        }
        .animation-delay-2000 {
          animation-delay: 2s;
        }
        .animation-delay-4000 {
          animation-delay: 4s;
        }
        @keyframes glow {
          0% {
            box-shadow: 0 0 5px rgba(147, 51, 234, 0.2), 0 0 10px rgba(147, 51, 234, 0.2);
          }
          50% {
            box-shadow: 0 0 20px rgba(147, 51, 234, 0.4), 0 0 30px rgba(147, 51, 234, 0.4);
          }
          100% {
            box-shadow: 0 0 5px rgba(147, 51, 234, 0.2), 0 0 10px rgba(147, 51, 234, 0.2);
          }
        }
        .animate-glow {
          animation: glow 2s infinite;
        }
        @keyframes fade-in {
          from {
            opacity: 0;
            transform: translateY(-20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        .animate-fade-in {
          animation: fade-in 1s ease-out;
        }
      `}</style>
    </div>
  );
};

export default Profile; 